import classNames from 'classnames'
import PropTypes from 'prop-types'

import InfiniteScroll from '../../infinite-scroll/InfiniteScroll'

import classes from './GridTable.module.scss'

const ALIGN_CLASSES = {
  left: classes.alignLeft,
  center: classes.alignCenter,
  right: classes.alignRight,
}

const GridTable = ({
  id, className, firstColumn, columns, rows, rowKey, hasMore = false, onFetchMore, hideLoader = false,
  onRowClick, selectedRowKey, rowClassName, skeletonRowsCount = 0, overlay, children,
}) => {
  const gridTemplateColumns = `${firstColumn.width} ${columns.map(c => c.gridWidth).join(' ')}`
  const contentId = `${id}Content`

  const renderRow = (row) => {
    const key = rowKey(row)
    const clickable = !!onRowClick

    return (
      <div
        key={key}
        className={classNames(classes.row, { [classes.clickable]: clickable, [classes.selected]: key === selectedRowKey }, rowClassName?.(row))}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? () => onRowClick(row) : undefined}
        onKeyDown={clickable ? () => null : undefined}
      >
        <div className={classNames(classes.col, classes.firstColumn)}>{firstColumn.render(row)}</div>
        {columns.map(column => (
          <div key={column.key} className={classNames(classes.col, ALIGN_CLASSES[column.align || 'left'])}>
            {column.render(row)}
          </div>
        ))}
      </div>
    )
  }

  const renderSkeletonRow = (idx) => (
    <div key={`skeleton-${idx}`} className={classes.row}>
      <div className={classNames(classes.col, classes.firstColumn)}>{firstColumn.skeleton}</div>
      {columns.map(column => (
        <div key={column.key} className={classNames(classes.col, ALIGN_CLASSES[column.align || 'left'])}>{column.skeleton}</div>
      ))}
    </div>
  )

  const grid = (
    <div id={contentId} className={classes.gridTable} style={{ gridTemplateColumns }}>
      <div className={classNames(classes.col, classes.header, classes.firstColumn, classes.topLeft, 'font-weight-bold')}>
        {firstColumn.header}
      </div>
      {columns.map(column => (
        <div key={column.key} className={classNames(classes.col, classes.header, ALIGN_CLASSES[column.align || 'left'], 'font-weight-bold')}>
          {column.header}
        </div>
      ))}

      {overlay || (
        <>
          {rows.map(renderRow)}
          {Array.from({ length: skeletonRowsCount }, (_, idx) => renderSkeletonRow(idx))}
        </>
      )}
    </div>
  )

  return (
    <div id={id} className={classNames(classes.table, className)}>
      {onFetchMore ? (
        <InfiniteScroll
          dataLength={rows.length}
          next={onFetchMore}
          hasMore={hasMore}
          scrollableTarget={contentId}
          className={classes.tableContent}
          hideLoader={hideLoader}
        >
          {grid}
        </InfiniteScroll>
      ) : grid}
      {children}
    </div>
  )
}

GridTable.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  firstColumn: PropTypes.shape({
    width: PropTypes.string.isRequired,
    header: PropTypes.node.isRequired,
    render: PropTypes.func.isRequired,
    skeleton: PropTypes.node,
  }).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    gridWidth: PropTypes.string.isRequired,
    header: PropTypes.node.isRequired,
    render: PropTypes.func.isRequired,
    skeleton: PropTypes.node,
    align: PropTypes.oneOf(['left', 'center', 'right']),
  })).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowKey: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  onFetchMore: PropTypes.func,
  hideLoader: PropTypes.bool,
  onRowClick: PropTypes.func,
  selectedRowKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rowClassName: PropTypes.func,
  skeletonRowsCount: PropTypes.number,
  overlay: PropTypes.node,
  children: PropTypes.node,
}

export default GridTable
